import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useAuthStore } from '@/stores/auth.ts';
import { useParticipantsStore } from '@/stores/participants.ts';
import { useSessionStateStore } from '@/stores/session-state.ts';

/**
 * Voting store - manages the current user's vote and vote statistics.
 */
export const useVotingStore = defineStore('voting', () => {
    const estimation = ref<string | number | null | undefined>(null);

    function syncLocalParticipantVote(value: number | string | null | undefined) {
        const authStore = useAuthStore();
        const participantsStore = useParticipantsStore();
        const userId = authStore.user?.id;
        if (userId) {
            participantsStore.updateParticipantVote(userId, value ?? null);
        }
    }

    function hydrateEstimation(value: number | string | null | undefined) {
        estimation.value = value;
        syncLocalParticipantVote(value);
    }

    // --- Actions ---

    function setEstimation(value: number | string) {
        if (estimation.value === value) return;
        estimation.value = value;
        syncLocalParticipantVote(value);

        // Send to server
        const sessionState = useSessionStateStore();
        sessionState.getWs().setEstimation(value);
    }

    function clearEstimation() {
        estimation.value = null;
        syncLocalParticipantVote(null);
    }

    // --- Computed ---

    const statistics = computed(() => {
        const participantsStore = useParticipantsStore();
        const votesCasted = participantsStore.voters.map((p) => p.vote).filter((v) => v !== null && v !== undefined);

        const groupedVotes: Record<string | number, number> = {};
        votesCasted.forEach((value) => {
            if (!groupedVotes[value]) groupedVotes[value] = 0;
            groupedVotes[value] = (groupedVotes[value] ?? 0) + 1;
        });

        const validVotes = votesCasted.filter((v) => !isNaN(Number(v))).map((v) => Number(v));
        const hasNumericVotes = validVotes.length > 0;
        const fibonacciOptions = [0, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89];

        function getMode(): string | number | null {
            if (Object.keys(groupedVotes).length === 0) return null;

            let max = -Infinity;
            let mode: string | number = Object.keys(groupedVotes)[0]!;
            for (const [value, c] of Object.entries(groupedVotes)) {
                if (c > max) {
                    max = c;
                    // Preserve original type: try to parse as number, otherwise keep as string
                    const numValue = Number(value);
                    mode = isNaN(numValue) ? value : numValue;
                }
            }

            return mode;
        }

        function getFibonacciMedianVote(votes: number[]): number | null {
            if (votes.length === 0) return null;

            const sorted = [...votes].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            const median = sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;

            let closest = fibonacciOptions[0]!;
            let minDiff = Math.abs(closest - median);

            for (const option of fibonacciOptions) {
                const diff = Math.abs(option - median);
                if (diff < minDiff) {
                    minDiff = diff;
                    closest = option;
                }
            }

            return closest;
        }

        const mode = getMode();
        const median = getFibonacciMedianVote(validVotes);

        // Suggestion logic: use median if available and greater than numeric mode, otherwise use mode
        let suggestion: string | number | null = mode;
        if (median !== null && typeof mode === 'number' && median > mode) {
            suggestion = median;
        } else if (median !== null && typeof mode !== 'number') {
            // If mode is non-numeric but we have numeric votes, use median
            suggestion = median;
        }

        return {
            votesCasted: votesCasted.length,
            groupedVotes,
            mode,
            median,
            hasNumericVotes,
            suggestion,
        };
    });

    function $reset() {
        estimation.value = null;
    }

    return {
        // State
        estimation,

        // Computed
        statistics,

        // Actions
        hydrateEstimation,
        setEstimation,
        clearEstimation,

        // Reset
        $reset,
    };
});
